import React, { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getAuthenticatedAxios, getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

// Sanitize locale to remove invalid modifiers like @posix
const sanitizeLocale = (locale) => {
  if (!locale) return 'en-US';
  // Remove @posix or other invalid modifiers
  return locale.split('@')[0];
};

const TradingChart = ({ tokenAddress, tokenSymbol }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  
  const [timeframe, setTimeframe] = useState('1h');
  const [loading, setLoading] = useState(false);

  const timeframes = [
    { label: '1m', value: '1m' },
    { label: '5m', value: '5m' },
    { label: '15m', value: '15m' },
    { label: '1H', value: '1h' },
    { label: '4H', value: '4h' },
    { label: '1D', value: '1d' },
  ];

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 500,
      layout: {
        backgroundColor: '#ffffff',
        textColor: '#333',
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' },
      },
      timeScale: {
        timeVisible: true,
        tickMarkFormatter: (time) => {
          try {
            const date = new Date(time * 1000);
            const safeLocale = sanitizeLocale(navigator.language);
            return new Intl.DateTimeFormat(safeLocale, { 
              month: 'short', 
              day: 'numeric' 
            }).format(date);
          } catch (error) {
            // Fallback to basic formatting
            const date = new Date(time * 1000);
            return date.toLocaleDateString('en-US');
          }
        }
      },
    });

    chartRef.current = chart;

    // Add candlestick series (v4.x API)
    const candleSeries = chart.addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    seriesRef.current = candleSeries;

    // Don't set mock data initially - wait for real data to load

    // Handle resize with safety check
    const handleResize = () => {
      try {
        if (chartContainerRef.current && chartRef.current) {
          chart.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      } catch (error) {
        // Silently ignore errors if chart is disposed
        console.debug('Chart resize error (chart may be disposed):', error);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      
      // Clean up refs first
      seriesRef.current = null;
      chartRef.current = null;
      
      // Then remove chart with try-catch to handle any disposal errors
      try {
        chart.remove();
      } catch (error) {
        console.debug('Chart removal error (already disposed):', error);
      }
    };
  }, []);

  // Load data when timeframe or token changes
  useEffect(() => {
    if (seriesRef.current && chartRef.current) {
      loadChartData();
    }
  }, [timeframe, tokenAddress]);

  const loadChartData = async () => {
    if (!tokenAddress || !seriesRef.current || !chartRef.current) return;
    
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(
        `${API_URL}/api/token/${tokenAddress}/ohlc`,
        {
          params: {
            timeframe: timeframe,
            limit: 100
          }
        }
      );

      const ohlcData = response.data?.data || response.data || [];
      
      console.log('BUYT OHLC Data loaded:', ohlcData.length, 'candles');
      if (ohlcData.length > 0) {
        console.log('First candle:', ohlcData[0]);
        console.log('Last candle:', ohlcData[ohlcData.length - 1]);
        // Log any null values for debugging
        const nullCandles = ohlcData.filter(c => 
          !c || c.time == null || c.open == null || c.high == null || c.low == null || c.close == null
        );
        if (nullCandles.length > 0) {
          console.warn(`Found ${nullCandles.length} candles with null values:`, nullCandles.slice(0, 3));
        }
      }
      
      // Check refs again before setting data (component might have unmounted)
      if (!seriesRef.current || !chartRef.current) {
        console.warn('Chart refs not available, skipping data update');
        return;
      }
      
      if (ohlcData.length > 0) {
        try {
          // Filter out invalid data (null, undefined, or missing required fields)
          const validData = ohlcData.filter(candle => {
            if (!candle) return false;
            const hasAllFields = (
              candle.time != null &&
              candle.open != null &&
              candle.high != null &&
              candle.low != null &&
              candle.close != null
            );
            const allNumbers = (
              typeof candle.time === 'number' &&
              typeof candle.open === 'number' &&
              typeof candle.high === 'number' &&
              typeof candle.low === 'number' &&
              typeof candle.close === 'number'
            );
            return hasAllFields && allNumbers;
          });
          
          console.log(`Filtered ${validData.length} valid candles from ${ohlcData.length} total`);
          
          if (validData.length > 0) {
            // Sort data by time in ascending order (lightweight-charts requirement)
            const sortedData = [...validData].sort((a, b) => a.time - b.time);
            
            // Final validation: ensure all values are finite numbers and positive
            // lightweight-charts expects time in seconds (Unix timestamp)
            // Also need to remove duplicate timestamps (common cause of "Value is null" error)
            const seenTimestamps = new Set();
            const finalData = sortedData.map(candle => {
              // Convert time to seconds if it's in milliseconds (timestamp > 1e10)
              let time = Number(candle.time);
              if (time > 1e10) {
                time = Math.floor(time / 1000);
              }
              time = Math.floor(time); // Ensure integer timestamp
              
              // Handle duplicate timestamps by incrementing
              let uniqueTime = time;
              while (seenTimestamps.has(uniqueTime)) {
                uniqueTime += 1; // Increment by 1 second to make unique
              }
              seenTimestamps.add(uniqueTime);
              
              const open = Number(candle.open);
              const high = Number(candle.high);
              const low = Number(candle.low);
              const close = Number(candle.close);
              
              // Ensure OHLC relationships are correct
              const finalHigh = Math.max(high, open, close);
              const finalLow = Math.min(low, open, close);
              
              return {
                time: uniqueTime,
                open: Number(open),
                high: Number(finalHigh),
                low: Number(Math.max(0.00001, finalLow)), // Ensure low is positive
                close: Number(close),
              };
            }).filter(candle => {
              const isValid = (
                isFinite(candle.time) &&
                isFinite(candle.open) &&
                isFinite(candle.high) &&
                isFinite(candle.low) &&
                isFinite(candle.close) &&
                candle.time > 0 &&
                candle.open > 0 &&
                candle.high > 0 &&
                candle.low > 0 &&
                candle.close > 0 &&
                candle.high >= Math.max(candle.open, candle.close) &&
                candle.low <= Math.min(candle.open, candle.close)
              );
              if (!isValid) {
                console.warn('Invalid candle filtered out:', candle);
              }
              return isValid;
            });
            
            console.log(`Final valid data: ${finalData.length} candles`);
            
            if (finalData.length > 0) {
              try {
                // Double-check that series is still available
                if (!seriesRef.current) {
                  console.warn('Series ref is null, cannot set data');
                  return;
                }
                seriesRef.current.setData(finalData);
                if (chartRef.current) {
                  chartRef.current.timeScale().fitContent();
                }
                console.log('Chart data set successfully');
              } catch (error) {
                console.error('Error setting chart data:', error);
                console.error('Problematic data sample:', finalData.slice(0, 3));
                // Fallback to mock data on error
                try {
                  const mockData = generateMockData();
                  if (seriesRef.current) {
                    seriesRef.current.setData(mockData);
                    if (chartRef.current) {
                      chartRef.current.timeScale().fitContent();
                    }
                  }
                } catch (fallbackError) {
                  console.error('Error setting fallback mock data:', fallbackError);
                }
              }
            } else {
              console.warn('No valid OHLC data after final validation, using mock data');
              try {
                const mockData = generateMockData();
                if (seriesRef.current) {
                  seriesRef.current.setData(mockData);
                  if (chartRef.current) {
                    chartRef.current.timeScale().fitContent();
                  }
                }
              } catch (error) {
                console.error('Error setting mock data:', error);
              }
            }
          } else {
            console.warn('No valid OHLC data after filtering, using mock data');
            try {
              const mockData = generateMockData();
              if (seriesRef.current) {
                seriesRef.current.setData(mockData);
                if (chartRef.current) {
                  chartRef.current.timeScale().fitContent();
                }
              }
            } catch (error) {
              console.error('Error setting mock data:', error);
            }
          }
        } catch (error) {
          console.error('Error processing chart data:', error);
          // Fallback to mock data
          try {
            const mockData = generateMockData();
            if (seriesRef.current) {
              seriesRef.current.setData(mockData);
              if (chartRef.current) {
                chartRef.current.timeScale().fitContent();
              }
            }
          } catch (fallbackError) {
            console.error('Error setting fallback mock data:', fallbackError);
          }
        }
      } else {
        // Use mock data
        try {
          const mockData = generateMockData();
          if (seriesRef.current) {
            seriesRef.current.setData(mockData);
            if (chartRef.current) {
              chartRef.current.timeScale().fitContent();
            }
          }
        } catch (error) {
          console.error('Error setting mock data (chart may be disposed):', error);
        }
      }

    } catch (error) {
      console.error('Error loading chart data:', error);
      // Show mock data on error
      if (seriesRef.current && chartRef.current) {
        try {
          const mockData = generateMockData();
          seriesRef.current.setData(mockData);
          chartRef.current.timeScale().fitContent();
        } catch (innerError) {
          console.debug('Error setting fallback data (chart may be disposed):', innerError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = () => {
    const data = [];
    const now = Math.floor(Date.now() / 1000);
    let price = 0.00012;

    for (let i = 100; i >= 0; i--) {
      const time = now - i * 3600;
      const open = Math.max(0.00001, price); // Ensure positive
      const change = (Math.random() - 0.5) * 0.00001;
      const close = Math.max(0.00001, price + change);
      
      // Ensure high >= max(open, close) and low <= min(open, close)
      const finalHigh = Math.max(open, close) * (1 + Math.abs(change) * 2);
      const finalLow = Math.min(open, close) * (1 - Math.abs(change) * 2);

      const candle = {
        time: Number(time),
        open: Number(open),
        high: Number(Math.max(finalHigh, open, close)),
        low: Number(Math.max(0.00001, Math.min(finalLow, open, close))),
        close: Number(close),
      };
      
      // Final validation before adding
      if (
        isFinite(candle.time) &&
        isFinite(candle.open) &&
        isFinite(candle.high) &&
        isFinite(candle.low) &&
        isFinite(candle.close) &&
        candle.time > 0 &&
        candle.open > 0 &&
        candle.high > 0 &&
        candle.low > 0 &&
        candle.close > 0 &&
        candle.high >= Math.max(candle.open, candle.close) &&
        candle.low <= Math.min(candle.open, candle.close)
      ) {
        data.push(candle);
      } else {
        console.warn('Invalid mock candle generated, skipping:', candle);
      }

      price = close;
    }

    if (data.length === 0) {
      // Fallback: create at least one valid candle
      const fallbackTime = Math.floor(Date.now() / 1000);
      data.push({
        time: fallbackTime,
        open: 0.00012,
        high: 0.00013,
        low: 0.00011,
        close: 0.00012,
      });
    }

    return data;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {tokenSymbol || 'Token'} Price Chart
            {loading && <span className="text-sm text-gray-500">(Loading...)</span>}
          </CardTitle>
          <div className="flex gap-1">
            {timeframes.map((tf) => (
              <Button
                key={tf.value}
                variant={timeframe === tf.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeframe(tf.value)}
              >
                {tf.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div ref={chartContainerRef} />
      </CardContent>
    </Card>
  );
};

export default TradingChart;
